import React, { useState, useEffect } from 'react';
import { config } from '../config';
import { useNavigate } from 'react-router-dom';
import { api, getAuthHeaders } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import './Invoices.css';

function Invoices({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [invoices, setInvoices] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [allRepairs, setAllRepairs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cars, setCars] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [emailInvoiceId, setEmailInvoiceId] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [groupByCustomer, setGroupByCustomer] = useState(false);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadInvoices();
      loadRepairs();
      loadCustomers();
      loadCars();
      loadTags();
    }
  }, [tenantId]);

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadInvoices = async () => {
    try {
      const data = await api.get('/invoices', tenantId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data.filter(r => !r.invoiceId && !r.invoiceIgnored));
      setAllRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : '';
  };

  const getCarInfo = (carId) => {
    const car = cars.find(c => c.id === carId);
    return car ? `${car.make} ${car.model}` : '';
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };


  const handleCreateFromRepair = async (repairId) => {
    try {
      const invoice = await api.post(`/invoices/from-repair/${repairId}`, {}, tenantId);
      showNotification('Rechnung erfolgreich erstellt', 'success');
      loadInvoices();
      loadRepairs();
      navigate(`/invoices?invoiceId=${invoice.id}`);
    } catch (error) {
      console.error('Error creating invoice:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unbekannter Fehler';
      showNotification(`Fehler beim Erstellen der Rechnung: ${errorMessage}`, 'error');
    }
  };

  const handleIgnoreRepair = async (repairId) => {
    try {
      await api.put(`/repairs/${repairId}`, { invoiceIgnored: true }, tenantId);
      showNotification('Reparatur wurde ignoriert', 'success');
      loadRepairs();
    } catch (error) {
      console.error('Error ignoring repair:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unbekannter Fehler';
      showNotification(`Fehler beim Ignorieren der Reparatur: ${errorMessage}`, 'error');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus }, tenantId);
      loadInvoices();
    } catch (error) {
      console.error('Error updating invoice status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Rechnung wirklich löschen?')) {
      try {
        await api.delete(`/invoices/${id}`, tenantId);
        loadInvoices();
        loadRepairs();
      } catch (error) {
        console.error('Error deleting invoice:', error);
      }
    }
  };

  return (
    <div className="invoices">
      <div className="page-header">
        <h1>Rechnungen</h1>
      </div>

      {repairs.length > 0 && (
        <div className="pending-repairs" style={{ marginBottom: '2rem' }}>
          <h2>Reparaturen ohne Rechnung</h2>
          <DataTable
            data={repairs}
            columns={[
              {
                key: 'actions',
                header: '',
                sortable: false,
                width: '180px',
                render: (row) => (
                  <div style={{ display: 'inline-flex', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => handleCreateFromRepair(row.id)}
                      className="btn-primary btn-small"
                    >
                      Rechnung erstellen
                    </button>
                    <button
                      onClick={() => handleIgnoreRepair(row.id)}
                      className="btn-secondary btn-small"
                      title="Ignorieren"
                    >
                      Ignorieren
                    </button>
                  </div>
                )
              },
              {
                key: 'customer',
                header: 'Kunde',
                sortType: 'string',
                accessor: (row) => {
                  const car = cars.find(c => c.id === row.carId);
                  if (!car) return '';
                  return getCustomerName(car.customerId);
                },
                render: (row) => {
                  const car = cars.find(c => c.id === row.carId);
                  if (!car || !car.customerId) return '-';
                  const customerName = getCustomerName(car.customerId);
                  if (!customerName) return '-';
                  return (
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/customers/${car.customerId}`)}
                    >
                      {customerName}
                    </button>
                  );
                }
              },
              {
                key: 'car',
                header: 'Auto',
                sortType: 'string',
                accessor: (row) => {
                  const car = cars.find(c => c.id === row.carId);
                  return car ? `${car.make} ${car.model}` : '';
                },
                render: (row) => {
                  const car = cars.find(c => c.id === row.carId);
                  if (!car) return '-';
                  return (
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/cars?carId=${car.id}`)}
                    >
                      {car.make} {car.model}
                    </button>
                  );
                }
              },
              {
                key: 'repairNumber',
                header: 'Reparatur',
                sortType: 'string',
                accessor: (row) => row.repairNumber || '',
                render: (row) => (
                  <button 
                    className="link-button" 
                    onClick={() => navigate(`/repairs/${row.id}`)}
                  >
                    {row.repairNumber || '-'}
                  </button>
                )
              },
              {
                key: 'totalAmount',
                header: 'Betrag',
                sortType: 'number',
                align: 'right',
                accessor: (row) => row.totalAmount || 0,
                render: (row) => `${(row.totalAmount || 0).toFixed(2)} ${currency}`
              }
            ]}
            defaultSort={{ key: 'totalAmount', direction: 'desc' }}
            emptyMessage="Keine Reparaturen ohne Rechnung"
          />
        </div>
      )}

      <div className="invoices-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Rechnungen</h2>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label>
                <input
                  type="checkbox"
                  checked={groupByCustomer}
                  onChange={(e) => setGroupByCustomer(e.target.checked)}
                />
                Nach Kunde gruppieren
              </label>
            </div>
            <select
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">Alle Kunden</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.firstName} {customer.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {groupByCustomer ? (
          <div>
            {customers.map(customer => {
              const customerInvoices = invoices.filter(inv => {
                const repair = allRepairs.find(r => r.id === inv.repairId);
                return repair && repair.customerId === customer.id;
              });
              
              if (customerInvoices.length === 0) return null;
              
              const totalAmount = customerInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
              
              return (
                <div key={customer.id} style={{ marginBottom: '2rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <h3 style={{ margin: 0 }}>
                      <button
                        type="button"
                        className="link-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customers/${customer.id}`);
                        }}
                        style={{ fontSize: '1.1em', fontWeight: 'bold' }}
                      >
                        {customer.firstName} {customer.lastName}
                      </button>
                    </h3>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <span style={{ color: '#666' }}>
                        {customerInvoices.length} Rechnung{customerInvoices.length !== 1 ? 'en' : ''}
                      </span>
                      <strong>
                        Gesamt: {totalAmount.toFixed(2)} {currency}
                      </strong>
                    </div>
                  </div>
                  <DataTable
                    data={customerInvoices}
                    getRowClassName={(row) => {
                      if (row.status === 'pending') return 'row-status-pending';
                      if (row.status === 'paid') return 'row-status-paid';
                      if (row.status === 'overdue') return 'row-status-overdue';
                      return '';
                    }}
                    columns={[
                      {
                        key: 'actions',
                        header: '',
                        sortable: false,
                        width: '140px',
                        render: (row) => (
                          <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                            {row.repairId && (
                              <IconButton
                                icon="preview"
                                onClick={() => navigate(`/repairs/${row.repairId}`)}
                                title="Reparatur anzeigen"
                                className="secondary"
                              />
                            )}
                            <IconButton
                              icon="invoice"
                              onClick={() => navigate(`/invoices/${row.id}`)}
                              title="Rechnung anzeigen"
                              className="primary"
                            />
                            <IconButton
                              icon="download"
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/invoices/${row.id}/pdf`, {
                                    headers: getAuthHeaders(tenantId)
                                  });
                                  if (!response.ok) {
                                    throw new Error(`HTTP error! status: ${response.status}`);
                                  }
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `Rechnung-${row.invoiceNumber}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (error) {
                                  console.error('Error downloading PDF:', error);
                                  showNotification('Fehler beim Herunterladen der PDF: ' + (error.message || 'Unbekannter Fehler', 'error'));
                                }
                              }}
                              title="PDF herunterladen"
                              className="secondary"
                            />
                          </div>
                        )
                      },
                      {
                        key: 'invoiceNumber',
                        header: 'Rechnungsnummer',
                        sortType: 'string',
                        accessor: (row) => row.invoiceNumber || '',
                        render: (row) => (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() => navigate(`/invoices/${row.id}`)}
                          >
                            {row.invoiceNumber || ''}
                          </button>
                        )
                      },
                      {
                        key: 'date',
                        header: 'Datum',
                        sortType: 'date',
                        accessor: (row) => row.date || '',
                        render: (row) => row.date ? new Date(row.date).toLocaleDateString('de-DE') : '-'
                      },
                      {
                        key: 'car',
                        header: 'Auto',
                        sortType: 'string',
                        accessor: (row) => {
                          const repair = allRepairs.find(r => r.id === row.repairId);
                          if (!repair) return '';
                          return getCarInfo(repair.carId);
                        },
                        render: (row) => {
                          const repair = allRepairs.find(r => r.id === row.repairId);
                          if (!repair || !repair.carId) return '-';
                          const carInfo = getCarInfo(repair.carId);
                          const mainContent = carInfo || '-';
                          
                          if (!row.tags || row.tags.length === 0) return mainContent;
                          
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              <div>{mainContent}</div>
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                                {row.tags.map(tagId => {
                                  const tag = tags.find(t => t.id === tagId);
                                  if (!tag) return null;
                                  return (
                                    <span
                                      key={tagId}
                                      style={{
                                        fontSize: '0.7rem',
                                        padding: '0.15rem 0.4rem',
                                        borderRadius: '20px',
                                        backgroundColor: tag.color,
                                        color: '#000',
                                        border: `1.5px solid ${tag.color}`,
                                        fontWeight: 600
                                      }}
                                    >
                                      {tag.name}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      },
                      {
                        key: 'grossAmount',
                        header: 'Brutto',
                        sortType: 'number',
                        align: 'right',
                        accessor: (row) => row.grossAmount || 0,
                        render: (row) => <strong>{row.grossAmount?.toFixed(2)} {row.currency}</strong>
                      },
                      {
                        key: 'status',
                        header: 'Status',
                        sortType: 'string',
                        accessor: (row) => row.status,
                        render: (row) => (
                          <select
                            value={row.status}
                            onChange={(e) => handleStatusChange(row.id, e.target.value)}
                            className="status-select"
                          >
                            <option value="pending">Ausstehend</option>
                            <option value="sent">Versendet</option>
                            <option value="paid">Bezahlt</option>
                            <option value="overdue">Überfällig</option>
                          </select>
                        )
                      },
                    ]}
                    defaultSort={{ key: 'date', direction: 'desc' }}
                    emptyMessage="Keine Rechnungen vorhanden"
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <DataTable
            data={selectedCustomerId ? invoices.filter(inv => {
              const repair = allRepairs.find(r => r.id === inv.repairId);
              return repair && repair.customerId === selectedCustomerId;
            }) : invoices}
          getRowClassName={(row) => {
            if (row.status === 'pending') return 'row-status-pending';
            if (row.status === 'paid') return 'row-status-paid';
            if (row.status === 'overdue') return 'row-status-overdue';
            return '';
          }}
          columns={[
            {
              key: 'actions',
              header: '',
              sortable: false,
              width: '140px',
              render: (row) => (
                <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                  {row.repairId && (
                    <IconButton
                      icon="preview"
                      onClick={() => navigate(`/repairs/${row.repairId}`)}
                      title="Reparatur anzeigen"
                      className="secondary"
                    />
                  )}
                  <IconButton
                    icon="invoice"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/invoices/${row.id}/pdf`, {
                          headers: getAuthHeaders(tenantId)
                        });
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Rechnung-${row.invoiceNumber}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error('Error downloading PDF:', error);
                        showNotification('Fehler beim Herunterladen der PDF: ' + (error.message || 'Unbekannter Fehler', 'error'));
                      }
                    }}
                    title="PDF herunterladen"
                    className="primary"
                  />
                  <IconButton
                    icon="email"
                    onClick={async () => {
                      try {
                        setEmailInvoiceId(row.id);
                        const response = await fetch(`${config.API_URL || 'https://api.garagedesk.net/api'}/invoices/${row.id}/pdf`, {
                          headers: getAuthHeaders(tenantId)
                        });
                        if (!response.ok) {
                          throw new Error(`HTTP error! status: ${response.status}`);
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Rechnung-${row.invoiceNumber}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                        
                        setTimeout(() => {
                          const repair = allRepairs.find(r => r.id === row.repairId);
                          const customer = repair ? customers.find(c => c.id === repair.customerId) : null;
                          const car = repair ? cars.find(c => c.id === repair.carId) : null;
                          const customerEmail = customer?.email || '';
                          const customerName = customer ? `${customer.firstName} ${customer.lastName}` : '';
                          const tenantName = tenant?.name || 'GarageDesk';
                          
                          const subject = encodeURIComponent(`Rechnung ${row.invoiceNumber} - ${tenantName}`);
                          const body = encodeURIComponent(
                            `Sehr geehrte/r ${customerName},\n\n` +
                            `anbei erhalten Sie die Rechnung ${row.invoiceNumber} vom ${row.date ? new Date(row.date).toLocaleDateString('de-DE') : ''}.\n\n` +
                            `Rechnungsbetrag: ${row.grossAmount?.toFixed(2)} ${row.currency || '€'}\n` +
                            (row.dueDate ? `Fälligkeitsdatum: ${new Date(row.dueDate).toLocaleDateString('de-DE')}\n` : '') +
                            `\nBitte überweisen Sie den Betrag auf unser Konto.\n\n` +
                            `Mit freundlichen Grüßen\n${tenantName}`
                          );
                          
                          const mailtoLink = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
                          window.location.href = mailtoLink;
                          setEmailInvoiceId(null);
                        }, 500);
                      } catch (error) {
                        console.error('Error preparing email:', error);
                        showNotification('Fehler beim Vorbereiten der E-Mail: ' + (error.message || 'Unbekannter Fehler', 'error'));
                        setEmailInvoiceId(null);
                      }
                    }}
                    disabled={(() => {
                      const repair = allRepairs.find(r => r.id === row.repairId);
                      const customer = repair ? customers.find(c => c.id === repair.customerId) : null;
                      return !customer?.email || emailInvoiceId === row.id;
                    })()}
                    title={(() => {
                      const repair = allRepairs.find(r => r.id === row.repairId);
                      const customer = repair ? customers.find(c => c.id === repair.customerId) : null;
                      return !customer?.email ? 'Keine E-Mail-Adresse vorhanden' : 'Rechnung per E-Mail versenden (PDF wird heruntergeladen, bitte als Anhang hinzufügen)';
                    })()}
                    className="secondary"
                  />
                  <IconButton
                    icon="delete"
                    onClick={() => handleDelete(row.id)}
                    title="Löschen"
                    className="danger"
                  />
                </div>
              )
            },
            {
              key: 'invoiceNumber',
              header: 'Rechnungsnummer',
              sortType: 'string',
              accessor: (row) => row.invoiceNumber,
              render: (row) => (
                <button 
                  className="link-button" 
                  onClick={() => navigate(`/invoices/${row.id}`)}
                >
                  {row.invoiceNumber}
                </button>
              )
            },
            {
              key: 'repair',
              header: 'Reparatur',
              sortType: 'string',
              accessor: (row) => {
                if (row.repairId) {
                  const repair = allRepairs.find(r => r.id === row.repairId);
                  return repair?.repairNumber || '';
                }
                return '';
              },
              render: (row) => {
                if (row.repairId) {
                  const repair = allRepairs.find(r => r.id === row.repairId);
                  if (repair && repair.repairNumber) {
                    return (
                      <button 
                        className="link-button" 
                        onClick={() => navigate(`/repairs/${row.repairId}`)}
                      >
                        {repair.repairNumber}
                      </button>
                    );
                  }
                }
                return <span>-</span>;
              }
            },
            {
              key: 'customer',
              header: 'Kunde',
              sortType: 'string',
              accessor: (row) => {
                const repair = allRepairs.find(r => r.id === row.repairId);
                if (!repair) return '';
                return getCustomerName(repair.customerId);
              },
              render: (row) => {
                const repair = allRepairs.find(r => r.id === row.repairId);
                if (!repair || !repair.customerId) return '-';
                const customerName = getCustomerName(repair.customerId);
                return customerName ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customers/${repair.customerId}`);
                    }}
                    style={{ textAlign: 'left' }}
                  >
                    {customerName}
                  </button>
                ) : '-';
              }
            },
            {
              key: 'car',
              header: 'Auto',
              sortType: 'string',
              accessor: (row) => {
                const repair = allRepairs.find(r => r.id === row.repairId);
                if (!repair) return '';
                return getCarInfo(repair.carId);
              },
              render: (row) => {
                const repair = allRepairs.find(r => r.id === row.repairId);
                if (!repair || !repair.carId) return '-';
                const carInfo = getCarInfo(repair.carId);
                return carInfo ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/cars/${repair.carId}`);
                    }}
                    style={{ textAlign: 'left' }}
                  >
                    {carInfo}
                  </button>
                ) : '-';
              }
            },
            {
              key: 'date',
              header: 'Datum',
              sortType: 'date',
              accessor: (row) => row.date ? new Date(row.date).toLocaleDateString('de-DE') : '',
              render: (row) => row.date ? new Date(row.date).toLocaleDateString('de-DE') : ''
            },
            {
              key: 'dueDate',
              header: 'Fälligkeitsdatum',
              sortType: 'date',
              accessor: (row) => row.dueDate ? new Date(row.dueDate).toLocaleDateString('de-DE') : '',
              render: (row) => row.dueDate ? new Date(row.dueDate).toLocaleDateString('de-DE') : ''
            },
            {
              key: 'netAmount',
              header: 'Netto',
              sortType: 'number',
              align: 'right',
              accessor: (row) => row.netAmount || 0,
              render: (row) => {
                const netAmount = row.netAmount || 0;
                return `${netAmount.toFixed(2)} ${currency}`;
              }
            },
            {
              key: 'taxAmount',
              header: 'MwSt.',
              sortType: 'number',
              align: 'right',
              accessor: (row) => row.taxAmount || 0,
              render: (row) => {
                const taxAmount = row.taxAmount || 0;
                return `${taxAmount.toFixed(2)} ${currency}`;
              }
            },
            {
              key: 'grossAmount',
              header: 'Brutto',
              sortType: 'number',
              align: 'right',
              accessor: (row) => row.grossAmount || 0,
              render: (row) => {
                const grossAmount = row.grossAmount || 0;
                return <strong>{grossAmount.toFixed(2)} {currency}</strong>;
              }
            },
            {
              key: 'status',
              header: 'Status',
              sortType: 'string',
              accessor: (row) => row.status,
              render: (row) => (
                <select
                  value={row.status}
                  onChange={(e) => handleStatusChange(row.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Ausstehend</option>
                  <option value="sent">Versendet</option>
                  <option value="paid">Bezahlt</option>
                  <option value="overdue">Überfällig</option>
                </select>
              )
            }
          ]}
          defaultSort={{ key: 'date', direction: 'desc' }}
          emptyMessage="Keine Rechnungen vorhanden"
        />
        )}
      </div>

    </div>
  );
}

export default Invoices;

